/**
 * Partnership Contract Service
 *
 * Handles all API calls related to partnership contracts.
 */
import apiClient from './apiClient';

export interface OnboardingSetup {
  partnerEmail: string;
  partnerPhone?: string;
  initialCapital: number;
  useTemplate: boolean;
  customProfitSplit?: {
    cp: number;
    op: number;
    reinvest: number;
  };
  customMaxStake?: number;
  targetMonthlyRevenue?: number;
  enableWeeklyCheckins: boolean;
}

export interface ContractSignature {
  signature_method: 'TYPED' | 'DRAWN' | 'OTP_VERIFIED';
  signature_data?: string;
  agreed_to_terms: boolean;
  agreed_to_esignature: boolean;
  otp_code?: string;
}

export interface ContractFilter {
  status?: string;
  skip?: number;
  limit?: number;
}

class ContractService {
  private baseURL = '/partnerships/contracts';

  /**
   * Quick setup contract during onboarding
   */
  async quickSetup(setup: OnboardingSetup) {
    const payload = {
      partner_email: setup.partnerEmail,
      partner_phone: setup.partnerPhone,
      initial_capital: setup.initialCapital,
      use_template: setup.useTemplate,
      custom_profit_split: setup.customProfitSplit,
      custom_max_stake: setup.customMaxStake,
      target_monthly_revenue: setup.targetMonthlyRevenue,
      enable_weekly_checkins: setup.enableWeeklyCheckins
    };

    const response = await apiClient.post(`${this.baseURL}/onboarding/quick-setup`, payload);
    return response.data;
  }

  /**
   * Get contract preview
   */
  async getPreview(contractId: string) {
    const response = await apiClient.get(`${this.baseURL}/onboarding/preview/${contractId}`);
    return response.data;
  }

  /**
   * Send contract for partner review
   */
  async sendForReview(contractId: string, reviewDeadlineDays: number = 7) {
    const response = await apiClient.post(
      `${this.baseURL}/onboarding/send-for-review/${contractId}`,
      null,
      { params: { review_deadline_days: reviewDeadlineDays } }
    );
    return response.data;
  }

  /**
   * Mark contract ready for signatures
   */
  async markReadyToSign(contractId: string) {
    const response = await apiClient.post(`${this.baseURL}/${contractId}/ready-to-sign`);
    return response.data;
  }

  /**
   * Sign contract
   */
  async signContract(contractId: string, signature: ContractSignature) {
    const payload = {
      contract_id: contractId,
      ...signature
    };

    const response = await apiClient.post(`${this.baseURL}/${contractId}/sign`, payload);
    return response.data;
  }

  /**
   * Get list of user's contracts
   */
  async getMyContracts(filter?: ContractFilter) {
    const response = await apiClient.get(this.baseURL, { params: filter });
    return response.data;
  }

  /**
   * Get contract details
   */
  async getContract(contractId: string) {
    const response = await apiClient.get(`${this.baseURL}/${contractId}`);
    return response.data;
  }

  /**
   * Update contract (before signing)
   */
  async updateContract(contractId: string, updates: any) {
    const response = await apiClient.patch(`${this.baseURL}/${contractId}`, updates);
    return response.data;
  }

  /**
   * Get contract signatures
   */
  async getSignatures(contractId: string) {
    const response = await apiClient.get(`${this.baseURL}/${contractId}/signatures`);
    return response.data;
  }

  /**
   * Propose amendment
   */
  async proposeAmendment(contractId: string, amendment: any) {
    const response = await apiClient.post(`${this.baseURL}/${contractId}/amendments`, amendment);
    return response.data;
  }

  /**
   * Approve amendment
   */
  async approveAmendment(contractId: string, amendmentId: string) {
    const response = await apiClient.post(
      `${this.baseURL}/${contractId}/amendments/${amendmentId}/approve`
    );
    return response.data;
  }

  /**
   * Create monthly capital snapshot
   */
  async createSnapshot(contractId: string, snapshot: any) {
    const response = await apiClient.post(`${this.baseURL}/${contractId}/snapshots`, snapshot);
    return response.data;
  }

  /**
   * Get capital snapshots
   */
  async getSnapshots(contractId: string, limit: number = 12) {
    const response = await apiClient.get(`${this.baseURL}/${contractId}/snapshots`, {
      params: { limit }
    });
    return response.data;
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats() {
    const response = await apiClient.get(`${this.baseURL}/dashboard/stats`);
    return response.data;
  }

  /**
   * Get contract notifications
   */
  async getNotifications(unreadOnly: boolean = false, skip: number = 0, limit: number = 50) {
    const response = await apiClient.get(`${this.baseURL}/notifications`, {
      params: { unread_only: unreadOnly, skip, limit }
    });
    return response.data;
  }

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string) {
    await apiClient.post(`${this.baseURL}/notifications/${notificationId}/read`);
  }

  /**
   * Download contract PDF
   */
  async downloadPDF(contractId: string) {
    const response = await apiClient.get(`${this.baseURL}/${contractId}/pdf`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `partnership-contract-${contractId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}

export const contractService = new ContractService();
